/**
 * Belt-and-suspenders client-side admin gate.
 *
 * Design reference: ``backend/docs/batch_19_design.md`` §9.0.
 *
 * Server is the hard gate (router-level ``requires("admin")``).
 * This component provides a fast client-side redirect so non-admin users
 * see the dashboard immediately without waiting for a 403 from the API.
 *
 * Only wraps routes that require admin role.
 */
import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { Spinner } from "@/components/common/Spinner";
import { useTenantV1Base } from "@/hooks/useTenantV1Base";

type Props = { children: ReactNode };

export function RequireAdmin({ children }: Props): JSX.Element {
  const v1Base = useTenantV1Base();
  const status = useAuthStore((state) => state.status);
  const role = useAuthStore((state) => state.role);

  if (status === "loading") {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === "anonymous" || role !== "admin") {
    return <Navigate to={`${v1Base}/dashboard`} replace />;
  }

  return <>{children}</>;
}
